/**
 * cart-quantity-flush.js
 *
 * component-cart-items.js debounces cart quantity writes by 300ms
 * (`#debouncedOnChange`). If the shopper bumps a quantity and then navigates
 * (clicks the product link in the drawer, hits Checkout, closes the tab)
 * inside that window, the `/cart/change` request is never sent and the change
 * is silently lost — the next page still shows the old quantity.
 *
 * This script records the shopper's intended quantity per line as they click,
 * clears it once the debounced write lands (`cart:update`), and force-flushes
 * anything still pending the moment the page starts to go away:
 *   - a keepalive POST on link click / form submit (request outlives the nav)
 *   - navigator.sendBeacon on pagehide / tab hidden (last resort)
 *
 * Absolute-quantity semantics via /cart/update, so a duplicate flush + the
 * debounced write are idempotent (both set the line to the same number).
 */
(function () {
  "use strict";

  /** line item key -> intended quantity, not yet confirmed in the cart */
  var pending = new Map();

  function cartUpdateUrl() {
    try {
      if (window.Theme && Theme.routes && Theme.routes.cart_update_url) {
        return Theme.routes.cart_update_url;
      }
    } catch (e) {}
    return "/cart/update";
  }

  /** Resolve a 1-based cart line number to its line item key from the DOM. */
  function keyForLine(line, fromNode) {
    var scope =
      (fromNode &&
        fromNode.closest &&
        fromNode.closest("cart-items-component, .ccd-items__wrapper, form")) ||
      document;
    var rows = scope.querySelectorAll(
      '[ref="cartItemRows[]"], li.ccd-item[data-key]'
    );
    var row = rows[line - 1];
    return row ? row.getAttribute("data-key") : null;
  }

  function buildUpdates() {
    var updates = {};
    pending.forEach(function (qty, key) {
      updates[key] = qty;
    });
    return updates;
  }

  document.addEventListener(
    "quantity-selector:update",
    function (event) {
      var detail = event.detail || {};
      // `cartLine` is only set for quantity selectors that sit on a cart line.
      if (!detail.cartLine || typeof detail.quantity !== "number") return;
      var key = keyForLine(detail.cartLine, event.target);
      if (key) pending.set(key, detail.quantity);
    },
    true
  );

  document.addEventListener("cart:update", function (event) {
    if (!pending.size) return;
    var items =
      event.detail && event.detail.data && event.detail.data.items
        ? event.detail.data.items
        : null;
    if (!items) {
      // The debounced write completed but we can't diff — assume it's settled.
      pending.clear();
      return;
    }
    var current = {};
    items.forEach(function (item) {
      current[item.key] = item.quantity;
    });
    pending.forEach(function (qty, key) {
      if (current[key] === qty) pending.delete(key);
    });
  });

  /** Still have a moment before navigation — send it with keepalive. */
  function flushKeepalive() {
    if (!pending.size) return;
    var body = JSON.stringify({ updates: buildUpdates() });
    pending.clear();
    try {
      fetch(cartUpdateUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: body,
        keepalive: true,
      });
    } catch (e) {}
  }

  /** Page is being torn down — sendBeacon is the only reliable option. */
  function flushBeacon() {
    if (!pending.size || !navigator.sendBeacon) return;
    var body = JSON.stringify({ updates: buildUpdates() });
    try {
      var ok = navigator.sendBeacon(
        cartUpdateUrl(),
        new Blob([body], { type: "application/json" })
      );
      if (ok) pending.clear();
    } catch (e) {}
  }

  function isPlainLeftClick(event) {
    return (
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    );
  }

  document.addEventListener(
    "click",
    function (event) {
      if (!pending.size || !(event.target instanceof Element)) return;
      if (!isPlainLeftClick(event)) return;
      var link = event.target.closest('a[href]:not([href^="#"])');
      if (!link || link.target === "_blank" || link.hasAttribute("download")) {
        return;
      }
      flushKeepalive();
    },
    true
  );

  document.addEventListener(
    "submit",
    function () {
      if (pending.size) flushKeepalive();
    },
    true
  );

  window.addEventListener("pagehide", flushBeacon);
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "hidden") flushBeacon();
  });
})();
